import './Experience.css';
import './../styles/styles.css';
import { Component } from 'react';

import Fade from 'react-reveal/Fade';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Parse a 'YYYY-MM' string into a zero-indexed { year, month }.
function parseMonth(value) {
  const [year, month] = value.split('-').map(Number);
  return { year, month: month - 1 };
}

function formatMonthYear(value) {
  const { year, month } = parseMonth(value);
  return `${MONTHS[month]} ${year}`;
}

// Duration between start and end (or today when end is null), counted
// inclusively (LinkedIn-style: both the start and end month count).
function durationLabel(startValue, endValue) {
  const start = parseMonth(startValue);
  let end;
  if (endValue) {
    end = parseMonth(endValue);
  } else {
    const now = new Date();
    end = { year: now.getFullYear(), month: now.getMonth() };
  }

  let months = (end.year - start.year) * 12 + (end.month - start.month) + 1;
  if (months < 1) months = 1;

  const years = Math.floor(months / 12);
  const remMonths = months % 12;
  const parts = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? 'yr' : 'yrs'}`);
  if (remMonths > 0) parts.push(`${remMonths} ${remMonths === 1 ? 'mo' : 'mos'}`);
  return parts.join(' ');
}

// e.g. "January 2024 — Present · 2 yrs 8 mos"
function formatDates(exp) {
  const startLabel = formatMonthYear(exp.start_date);
  const endLabel = exp.end_date ? formatMonthYear(exp.end_date) : 'Present';
  return `${startLabel} — ${endLabel} · ${durationLabel(exp.start_date, exp.end_date)}`;
}

const experiences = [
  {
    initials: 'WDS',
    color: '#e69c50',
    start_date: '2024-01',
    end_date: null,
    title: 'Developer',
    company: 'Wern Digital Solutions · Full-time',
    sections: [
      {
        bullets: [
          'Design and build custom software for B2B clients — commerce platforms, supply-chain systems and business tools that streamline how they operate.',
          'Build robust APIs and third-party integrations with REST, GraphQL and webhooks, connecting the platforms clients depend on.',
          'Work closely with clients to understand their goals and deliver tailored solutions that make a measurable difference.',
        ],
      },
    ],
  },
  {
    initials: 'TS',
    color: '#033D7A',
    start_date: '2023-04',
    end_date: null,
    title: 'Business Manager',
    company: 'Tanat Supplies · Full-time',
    location: 'Oswestry, England, United Kingdom · On-site',
    sections: [
      {
        bullets: [
          'Manage supply-chain operations end to end, ensuring fast, reliable fulfilment and happy customers.',
          'Oversee finance and purchasing to keep the business running efficiently and profitably.',
          'Introduced technology upgrades that streamlined workflows and lifted day-to-day efficiency.',
          'Designed and built a custom invoice generator now used to produce thousands of invoices — saving hours of manual work.',
        ],
      },
    ],
  },
  {
    initials: 'G',
    color: '#22FF7D',
    start_date: '2022-08',
    end_date: '2023-11',
    title: 'Web & App Developer',
    company: 'gloversure · Full-time',
    location: 'Welshpool, Wales, United Kingdom',
    sections: [
      {
        bullets: [
          'Led multiple client projects as project lead, delivering bespoke mobile apps and web portals from concept to launch.',
          'Apps I built have been downloaded by over 10,000 users.',
          'Delivered polished, high-quality solutions with Laravel, React, Ionic and Angular.',
          'Partnered closely with clients to understand their needs and keep every project on track.',
          'Owned project timelines and deliverables, sharpening strong problem-solving skills along the way.',
        ],
      },
    ],
  },
  {
    initials: 'G',
    color: '#22FF7D',
    start_date: '2020-07',
    end_date: '2021-08',
    title: 'Mobile App Developer — Hybrid Development iOS/Android',
    company: 'gloversure · Internship',
    location: 'Chester, England, United Kingdom',
    description: "Completed as the Year in Industry placement of my degree — a 12-month placement I extended through the summer before returning for my final year. I worked across a broad stack including JavaScript (Angular, Vue) and PHP (Laravel), primarily as an app developer with Ionic and Angular, while also contributing to web-development projects.",
    sections: [
      {
        heading: 'Skills gained',
        bullets: [
          'Database structure planning',
          'API endpoint creation',
          'App Development',
          'Web Development',
          'Web/App styling (CSS/SCSS)',
          'Planning & Designing',
          'Documenting development progress',
          'Time estimating for new app features',
          'Collaborating with team members',
          'Working independently',
          'Testing',
          'Bug fixing/customer support',
        ],
      },
    ],
  },
  {
    initials: 'G',
    color: '#22FF7D',
    start_date: '2019-08',
    end_date: '2019-09',
    title: '2019 Work Experience',
    company: 'gloversure',
    sections: [
      {
        heading: 'App Developer · September 2019 · Greater Cheshire West and Chester Area (1 day)',
        text: 'Introduced to React and the Ionic framework, building web apps packaged for deployment as multi-platform mobile apps.',
      },
      {
        heading: 'Web Developer · August 2019 · Welshpool (1 week)',
        text: 'Shadowed developers across a range of projects — a first hands-on look at server management, PHP, and web development with WordPress.',
      },
    ],
  },
];

const VISIBLE_COUNT = 3;

class Experience extends Component {
  constructor(props) {
    super(props);
    this.state = { expanded: false };
    this.toggleExpanded = this.toggleExpanded.bind(this);
  }

  toggleExpanded() {
    this.setState((state) => ({ expanded: !state.expanded }));
  }

  renderItem(exp, index) {
    return <Fade bottom key={index}>
      <div className="exp-item">
        <div className="exp-timeline-col">
          <span className="exp-dot"></span>
          <span className="exp-line"></span>
        </div>
        <div className="exp-content">
          <div className="exp-badge" style={{ backgroundColor: exp.color }}>{exp.initials}</div>
          <p className="exp-dates">{formatDates(exp)}</p>
          <h3 className="exp-title">{exp.title}</h3>
          {exp.company && <p className="exp-company">{exp.company}</p>}
          {exp.location && <p className="exp-location">{exp.location}</p>}
          {exp.description && <p className="exp-description">{exp.description}</p>}
          {exp.sections && exp.sections.map((section, si) =>
            <div className="exp-section" key={si}>
              {section.heading && <p className="exp-section-heading">{section.heading}</p>}
              {section.text && <p className="exp-section-text">{section.text}</p>}
              {section.bullets &&
                <ul>
                  {section.bullets.map((bullet, bi) => <li key={bi}>{bullet}</li>)}
                </ul>}
            </div>)}
        </div>
      </div>
    </Fade>;
  }

  render () {
    const visible = experiences.slice(0, VISIBLE_COUNT);
    const hidden = experiences.slice(VISIBLE_COUNT);

    return <div id="experience" className="page-padding-first white-background">
      <Fade bottom>
        <div className="experience-title-div">
          <h1 className="title-h1 dark">Experience</h1>
          <p className="title-p light">where I've been building</p>
        </div>
      </Fade>
      <div className="experience-timeline">
        {visible.map((exp, i) => this.renderItem(exp, i))}
        {this.state.expanded && hidden.map((exp, i) => this.renderItem(exp, i + VISIBLE_COUNT))}
        <div className="experience-toggle-wrap">
          <button className="experience-toggle" onClick={this.toggleExpanded}>
            {this.state.expanded ? 'See less' : 'See more'}
          </button>
        </div>
      </div>
    </div>;
  }
}

export default Experience;
